import { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  useTheme,
} from '@mui/material';
import { clearToken } from '../utils/Authe';
import LockClockIcon from '@mui/icons-material/LockClock';

const IdleTimerContext = createContext();

export const IdleTimerProvider = ({ children, timeoutMinutes = 60 }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [isLoggedOut, setIsLoggedOut] = useState(false);

  useEffect(() => {
    let timeout;
    const resetTimeout = () => {
      clearTimeout(timeout);
      timeout = setTimeout(async () => {
        setIsLoggedOut(true);
        const clientCode = sessionStorage.getItem('clientCode');
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        try {
          const response = await fetch(`${baseUrl}/student/logout/`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${sessionStorage.getItem("student_token")}`,
            },
          });
          if (response.ok) {
            clearToken("student_token");
            sessionStorage.removeItem("student_id");
            sessionStorage.removeItem("student_name");
            sessionStorage.removeItem("student_email");
          } else {
            console.error("Logout failed");
          }
        } catch (error) {
          console.error("Error during logout:", error);
        }
      }, timeoutMinutes * 60 * 1000);
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    const handleEvent = () => resetTimeout();
    events.forEach((event) => window.addEventListener(event, handleEvent));
    resetTimeout();

    return () => {
      clearTimeout(timeout);
      events.forEach((event) => window.removeEventListener(event, handleEvent));
    };
  }, [navigate, timeoutMinutes]);

  const handleSignInAgain = () => {
    setIsLoggedOut(false);
    const clientCode = sessionStorage.getItem('clientCode');
    navigate(`/login?client-code=${clientCode}`);
  };

  return (
    <IdleTimerContext.Provider value={{ isLoggedOut, setIsLoggedOut }}>
      {children}
      <Dialog
        open={isLoggedOut}
        disableEscapeKeyDown
        onClose={() => {}}
        PaperProps={{
          sx: {
            borderRadius: '24px',
            maxWidth: '420px',
            width: '100%',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 24px 48px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)'
              : '0 24px 48px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.05)',
            overflow: 'hidden',
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)'
              : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            position: 'relative',
          },
        }}
        sx={{
          backdropFilter: 'blur(16px)',
          zIndex: 1400,
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
          },
        }}
      >
        {/* Decorative gradient orb */}
        <Box
          sx={{
            position: 'absolute',
            top: '-80px',
            right: '-80px',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%)',
            pointerEvents: 'none',
            filter: 'blur(40px)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '-60px',
            left: '-60px',
            width: '160px',
            height: '160px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(30, 139, 175, 0.12) 0%, transparent 70%)',
            pointerEvents: 'none',
            filter: 'blur(40px)',
          }}
        />
        {/* Content Container */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            p: 4,
            textAlign: 'center',
          }}
        >
          {/* Icon with animated background */}
          <Box
            sx={{
              width: '72px',
              height: '72px',
              margin: '0 auto 24px',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(30, 139, 175, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(30, 139, 175, 0.08) 100%)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(59, 130, 246, 0.2)'
                : '1px solid rgba(59, 130, 246, 0.15)',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: '-2px',
                borderRadius: '20px',
                padding: '2px',
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3), rgba(30, 139, 175, 0.3))',
                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
                opacity: 0.6,
              },
            }}
          >
            <LockClockIcon
              sx={{
                fontSize: '36px',
                color: theme.palette.mode === 'dark' ? '#60a5fa' : '#3b82f6',
                filter: 'drop-shadow(0 2px 4px rgba(59, 130, 246, 0.3))',
              }}
            />
          </Box>
          {/* Title */}
          <DialogTitle
            sx={{
              color: theme.palette.mode === 'dark' ? '#f1f5f9' : '#0f172a',
              fontWeight: 700,
              fontSize: '1.75rem',
              p: 0,
              mb: 1,
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
            }}
          >
            Session Expired
          </DialogTitle>
          {/* Subtitle with icon */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              mb: 3,
              px: 2,
              py: 0.75,
              borderRadius: '12px',
              background: theme.palette.mode === 'dark'
                ? 'rgba(248, 113, 113, 0.1)'
                : 'rgba(239, 68, 68, 0.06)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(248, 113, 113, 0.2)'
                : '1px solid rgba(239, 68, 68, 0.12)',
            }}
          >
            <Box
              sx={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: '#ef4444',
                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.5 },
                },
              }}
            />
            <Typography
              sx={{
                color: theme.palette.mode === 'dark' ? '#fca5a5' : '#dc2626',
                fontSize: '0.875rem',
                fontWeight: 600,
                letterSpacing: '0.01em',
              }}
            >
              Inactive for {timeoutMinutes} minutes
            </Typography>
          </Box>
          {/* Description */}
          <Typography
            sx={{
              color: theme.palette.mode === 'dark'
                ? 'rgba(241, 245, 249, 0.7)'
                : 'rgba(15, 23, 42, 0.65)',
              fontSize: '0.9375rem',
              lineHeight: 1.6,
              mb: 3,
              px: 1,
              fontWeight: 400,
            }}
          >
            For your security, you've been automatically logged out. Please sign in again to continue where you left off.
          </Typography>
          {/* Action Button */}
          <DialogActions sx={{ justifyContent: 'center', p: 0 }}>
            <Button
              onClick={handleSignInAgain}
              variant="contained"
              fullWidth
              sx={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #1e8baf 100%)',
                color: 'white',
                fontWeight: 600,
                py: 1.5,
                borderRadius: '14px',
                boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 100%)',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                },
                '&:hover': {
                  background: 'linear-gradient(135deg, #2563eb 0%, #1a7a9a 100%)',
                  boxShadow: '0 8px 24px rgba(59, 130, 246, 0.4)',
                  transform: 'translateY(-2px)',
                  '&::before': {
                    opacity: 1,
                  },
                },
                '&:active': {
                  transform: 'translateY(0px)',
                },
                textTransform: 'none',
                fontSize: '1rem',
                letterSpacing: '0.01em',
              }}
            >
              Sign In Again
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </IdleTimerContext.Provider>
  );
};

export const useIdleTimer = () => useContext(IdleTimerContext);
